import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  LinearProgress,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Grid,
  Stack,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';

interface PersonalInfo {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  workAuthorization: string;
  email: string;
  confirmEmail: string;
  phone: string;
  confirmPhone: string;
  fullAddress: string;
}

interface WorkExperience {
  id: string;
  clientName: string;
  website: string;
  fromDate: string;
  toDate: string;
  isPresent: boolean;
  jobTitle: string;
  jobDescription: string;
  managerName: string;
  managerEmail: string;
  managerPhone: string;
  managerLinkedIn: string;
}

interface Education {
  id: string;
  institutionName: string;
  level: string;
  fieldOfStudy: string;
  fromMonthYear: string;
  toMonthYear: string;
  gpaScore: string;
}

interface DocumentCapture {
  livePhoto: string | null;
  idDocument: string | null;
  ssnDocument: string | null;
  extractedData: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    address: string;
    ssnLast4: string;
  };
}

const USATechieSignup: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form data
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    workAuthorization: '',
    email: '',
    confirmEmail: '',
    phone: '',
    confirmPhone: '',
    fullAddress: '',
  });

  const [workExperiences, setWorkExperiences] = useState<WorkExperience[]>([]);
  const [educations, setEducations] = useState<Education[]>([]);
  const [documentCapture, setDocumentCapture] = useState<DocumentCapture>({
    livePhoto: null,
    idDocument: null,
    ssnDocument: null,
    extractedData: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      address: '',
      ssnLast4: '',
    }
  });

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState<'live' | 'id' | 'ssn'>('live');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Dialog states
  const [showWorkExperienceDialog, setShowWorkExperienceDialog] = useState(false);
  const [showEducationDialog, setShowEducationDialog] = useState(false);
  const [showFraudWarning, setShowFraudWarning] = useState(false);
  const [showEducationWarning, setShowEducationWarning] = useState(false);

  const steps = [
    'Country Selection',
    'Document Verification',
    'Personal Information',
    'Work Experience',
    'Education',
    'Review & Submit'
  ];

  const workAuthorizationOptions = [
    'US Citizen',
    'Green Card',
    'GC EAD',
    'H1B',
    'H4 EAD',
    'OPT EAD',
    'STEM OPT EAD',
    'CPT',
    'L1',
    'L2',
    'TN',
    'Other'
  ];

  const educationLevels = [
    'PhD',
    'Masters',
    'Bachelors',
    'Diploma',
    '+12 (Intermediate)',
    'High School'
  ];

  // Camera functions
  const startCamera = async (type: 'live' | 'id' | 'ssn') => {
    try {
      setCameraType(type);
      setShowCamera(true);
      setCapturing(false);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      setError('Camera access denied. Please allow camera access to continue.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      setCapturing(true);
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (context) {
        context.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        
        // Simulate OCR processing
        setTimeout(() => {
          setDocumentCapture(prev => ({
            ...prev,
            [cameraType === 'live' ? 'livePhoto' : 
             cameraType === 'id' ? 'idDocument' : 'ssnDocument']: imageData,
            extractedData: {
              ...prev.extractedData,
              firstName: cameraType === 'id' ? 'John' : prev.extractedData.firstName,
              lastName: cameraType === 'id' ? 'Doe' : prev.extractedData.lastName,
              dateOfBirth: cameraType === 'id' ? '01/15/1990' : prev.extractedData.dateOfBirth,
              address: cameraType === 'id' ? '123 Main St, New York, NY 10001' : prev.extractedData.address,
              ssnLast4: cameraType === 'ssn' ? '1234' : prev.extractedData.ssnLast4,
            }
          }));
          
          // Auto-fill personal info if ID document
          if (cameraType === 'id') {
            setPersonalInfo(prev => ({
              ...prev,
              firstName: 'John',
              lastName: 'Doe',
              dateOfBirth: '01/15/1990',
              fullAddress: '123 Main St, New York, NY 10001',
            }));
          }
          
          setCapturing(false);
          stopCamera();
          setSuccess(`${cameraType === 'live' ? 'Live photo' : 
                      cameraType === 'id' ? 'ID document' : 'SSN card'} captured successfully!`);
        }, 2000);
      }
    }
  };

  // Generate VerTechie ID
  const generateVerTechieId = () => {
    const { firstName, dateOfBirth, ssnLast4 } = documentCapture.extractedData;
    if (firstName && dateOfBirth && ssnLast4) {
      const first3Letters = firstName.substring(0, 3).toUpperCase();
      const [month, day, year] = dateOfBirth.split('/');
      return `${first3Letters}${month}${day}${year}${ssnLast4}`;
    }
    return null;
  };

  // Check if VerTechie ID exists
  const checkVerTechieId = async (id: string) => {
    // Simulate API call
    return new Promise<{ exists: boolean; status: 'active' | 'blocked' | 'new' }>((resolve) => {
      setTimeout(() => {
        // Simulate some IDs exist
        const existingIds = ['JOH011519901234', 'JAN122519951234'];
        if (existingIds.includes(id)) {
          resolve({ exists: true, status: 'active' });
        } else {
          resolve({ exists: false, status: 'new' });
        }
      }, 1000);
    });
  };

  // Work experience functions
  const addWorkExperience = () => {
    setShowFraudWarning(true);
  };

  const handleAddWorkExperience = () => {
    setShowFraudWarning(false);
    setShowWorkExperienceDialog(true);
  };

  const saveWorkExperience = (experience: Omit<WorkExperience, 'id'>) => {
    const newExperience: WorkExperience = {
      ...experience,
      id: Date.now().toString(),
    };
    setWorkExperiences(prev => [...prev, newExperience]);
    setShowWorkExperienceDialog(false);
  };

  // Education functions
  const addEducation = () => {
    setShowEducationWarning(true);
  };

  const handleAddEducation = () => {
    setShowEducationWarning(false);
    setShowEducationDialog(true);
  };

  const saveEducation = (education: Omit<Education, 'id'>) => {
    const newEducation: Education = {
      ...education,
      id: Date.now().toString(),
    };
    setEducations(prev => [...prev, newEducation]);
    setShowEducationDialog(false);
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      // Check VerTechie ID
      const vertechieId = generateVerTechieId();
      if (vertechieId) {
        setLoading(true);
        const result = await checkVerTechieId(vertechieId);
        if (result.exists) {
          setError(`You are already ${result.status}. Please login to your account or contact review@vertechie.com if you believe this is an error.`);
          setLoading(false);
          return;
        }
        setSuccess(`Your VerTechie ID: ${vertechieId}`);
      }
    }
    
    setActiveStep(prev => prev + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSuccess('Registration submitted successfully! You will receive a verification email shortly.');
      setTimeout(() => {
        navigate('/signup-success');
      }, 3000);
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Welcome to VerTechie USA Registration
            </Typography>
            <Typography variant="body1" paragraph>
              You've selected the USA registration process. This will include:
            </Typography>
            <Box sx={{ mb: 3 }}>
              {[
                'Live photo verification with head movement detection',
                'US Government ID verification (DL, State ID, or Passport)',
                'Social Security Number verification',
                'Work authorization status verification',
                'Employment and education verification'
              ].map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> All documents will be verified through official channels. 
                Any false information will result in permanent platform ban and legal action.
              </Typography>
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Document Verification
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <CameraIcon />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      Live Photo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Look at the camera and follow the instructions to move your head
                    </Typography>
                    {documentCapture.livePhoto ? (
                      <Box>
                        <img 
                          src={documentCapture.livePhoto} 
                          alt="Live photo" 
                          style={{ width: '100%', maxWidth: 200, borderRadius: 8 }}
                        />
                        <Chip 
                          icon={<CheckCircleIcon />} 
                          label="Captured" 
                          color="success" 
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    ) : (
                      <Button 
                        variant="outlined" 
                        startIcon={<CameraIcon />}
                        onClick={() => startCamera('live')}
                        fullWidth
                      >
                        Capture Live Photo
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Avatar sx={{ bgcolor: 'secondary.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <BadgeIcon />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      Government ID
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Show your DL, State ID, or US Passport to the camera
                    </Typography>
                    {documentCapture.idDocument ? (
                      <Box>
                        <img 
                          src={documentCapture.idDocument} 
                          alt="ID document" 
                          style={{ width: '100%', maxWidth: 200, borderRadius: 8 }}
                        />
                        <Chip 
                          icon={<CheckCircleIcon />} 
                          label="Captured" 
                          color="success" 
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    ) : (
                      <Button 
                        variant="outlined" 
                        startIcon={<CameraIcon />}
                        onClick={() => startCamera('id')}
                        fullWidth
                      >
                        Capture ID Document
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent>
                    <Avatar sx={{ bgcolor: 'warning.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                      <SecurityIcon />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      SSN Card
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Hide first 5 digits, show last 4 digits clearly
                    </Typography>
                    {documentCapture.ssnDocument ? (
                      <Box>
                        <img 
                          src={documentCapture.ssnDocument} 
                          alt="SSN document" 
                          style={{ width: '100%', maxWidth: 200, borderRadius: 8 }}
                        />
                        <Chip 
                          icon={<CheckCircleIcon />} 
                          label="Captured" 
                          color="success" 
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    ) : (
                      <Button 
                        variant="outlined" 
                        startIcon={<CameraIcon />}
                        onClick={() => startCamera('ssn')}
                        fullWidth
                      >
                        Capture SSN Card
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {documentCapture.extractedData.firstName && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Data extracted successfully!</strong><br />
                  Name: {documentCapture.extractedData.firstName} {documentCapture.extractedData.lastName}<br />
                  DOB: {documentCapture.extractedData.dateOfBirth}<br />
                  Address: {documentCapture.extractedData.address}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Personal Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  value={personalInfo.firstName}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  value={personalInfo.middleName}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, middleName: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  value={personalInfo.lastName}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth *"
                  placeholder="MM/DD/YYYY"
                  value={personalInfo.dateOfBirth}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Work Authorization *</InputLabel>
                  <Select
                    value={personalInfo.workAuthorization}
                    onChange={(e) => setPersonalInfo(prev => ({ ...prev, workAuthorization: e.target.value }))}
                    label="Work Authorization *"
                  >
                    {workAuthorizationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address *"
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Email Address *"
                  type="email"
                  value={personalInfo.confirmEmail}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, confirmEmail: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={personalInfo.phone}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Phone Number *"
                  value={personalInfo.confirmPhone}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, confirmPhone: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Address *"
                  multiline
                  rows={3}
                  value={personalInfo.fullAddress}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, fullAddress: e.target.value }))}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Work Experience
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addWorkExperience}
                sx={{ borderRadius: 2 }}
              >
                Add Experience
              </Button>
            </Box>

            {workExperiences.length === 0 ? (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Work Experience Added
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Add your work experience to help employers understand your background
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {workExperiences.map((exp, index) => (
                  <Card key={exp.id} sx={{ p: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {exp.jobTitle} at {exp.clientName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {exp.fromDate} - {exp.isPresent ? 'Present' : exp.toDate}
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {exp.jobDescription}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Manager: {exp.managerName} ({exp.managerEmail})
                          </Typography>
                        </Box>
                        <IconButton 
                          color="error" 
                          onClick={() => setWorkExperiences(prev => prev.filter(e => e.id !== exp.id))}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Education
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addEducation}
                sx={{ borderRadius: 2 }}
              >
                Add Education
              </Button>
            </Box>

            {educations.length === 0 ? (
              <Card sx={{ textAlign: 'center', py: 4 }}>
                <CardContent>
                  <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No Education Added
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Add your educational background to complete your profile
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {educations.map((edu, index) => (
                  <Card key={edu.id} sx={{ p: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {edu.level} in {edu.fieldOfStudy}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {edu.institutionName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {edu.fromMonthYear} - {edu.toMonthYear}
                            {edu.gpaScore && ` • GPA: ${edu.gpaScore}`}
                          </Typography>
                        </Box>
                        <IconButton 
                          color="error" 
                          onClick={() => setEducations(prev => prev.filter(e => e.id !== edu.id))}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Review & Submit
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Name:</strong> {personalInfo.firstName} {personalInfo.middleName} {personalInfo.lastName}<br />
                  <strong>DOB:</strong> {personalInfo.dateOfBirth}<br />
                  <strong>Work Authorization:</strong> {personalInfo.workAuthorization}<br />
                  <strong>Email:</strong> {personalInfo.email}<br />
                  <strong>Phone:</strong> {personalInfo.phone}<br />
                  <strong>Address:</strong> {personalInfo.fullAddress}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Work Experience ({workExperiences.length})
                </Typography>
                {workExperiences.map((exp, index) => (
                  <Typography key={exp.id} variant="body2" paragraph>
                    <strong>{exp.jobTitle}</strong> at {exp.clientName} ({exp.fromDate} - {exp.isPresent ? 'Present' : exp.toDate})
                  </Typography>
                ))}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Education ({educations.length})
                </Typography>
                {educations.map((edu, index) => (
                  <Typography key={edu.id} variant="body2" paragraph>
                    <strong>{edu.level}</strong> in {edu.fieldOfStudy} from {edu.institutionName}
                  </Typography>
                ))}
              </CardContent>
            </Card>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                By submitting this form, you agree to our Terms of Service and confirm that all information provided is accurate and truthful.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton onClick={() => navigate('/techie-signup')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              USA Tech Professional Registration
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {loading && <LinearProgress sx={{ mb: 3 }} />}

          <Box sx={{ mb: 4 }}>
            {renderStepContent()}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              endIcon={activeStep === steps.length - 1 ? undefined : <ArrowForwardIcon />}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                'Submit Registration'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onClose={stopCamera} maxWidth="md" fullWidth>
        <DialogTitle>
          {cameraType === 'live' ? 'Live Photo Capture' : 
           cameraType === 'id' ? 'ID Document Capture' : 'SSN Card Capture'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxWidth: 500, borderRadius: 8 }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            
            {cameraType === 'live' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Please look at the camera and follow the instructions to move your head
              </Typography>
            )}
            
            {cameraType === 'id' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Show your government ID (DL, State ID, or Passport) to the camera
              </Typography>
            )}
            
            {cameraType === 'ssn' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Hide the first 5 digits of your SSN, show only the last 4 digits clearly
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopCamera}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={capturePhoto}
            disabled={capturing}
            startIcon={capturing ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
          >
            {capturing ? 'Capturing...' : 'Capture'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fraud Warning Dialog */}
      <Dialog open={showFraudWarning} onClose={() => setShowFraudWarning(false)} maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Important Notice
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Please give us 100% accurate information. Any falsified or fake information will definitely lead to big problems. We will directly reach out to our internal sources and company officials to check your employment history including job title, job duties, job tenure. Any small fake information will lead to disqualification and permanent block to our platform.
          </Typography>
          <Typography variant="body1" paragraph>
            We will keep your photo, name, and the full reason for blocking you in our blocked list data and we give that access to all of our partnered clients and vendors. By clicking "Agree and Proceed" you are agreeing to this.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: 'error.main' }}>
            We take legal actions against fraudulent information.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFraudWarning(false)}>Go Back</Button>
          <Button variant="contained" color="error" onClick={handleAddWorkExperience}>
            Agree and Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Education Warning Dialog */}
      <Dialog open={showEducationWarning} onClose={() => setShowEducationWarning(false)} maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Education Verification Notice
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Please mention the complete genuine details because we directly verify these details with the official institution in person, and we compare each and every single detail with them.
          </Typography>
          <Typography variant="body1" paragraph>
            If you lie, you are out of the platform and your blocked data lives in our blocklisted data that is available for our subscribed clients forever, so you won't get opportunities with them as well for sure.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: 'error.main' }}>
            Please provide legit details.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEducationWarning(false)}>Go Back</Button>
          <Button variant="contained" color="error" onClick={handleAddEducation}>
            Yes, I Read and Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Work Experience Dialog */}
      <WorkExperienceDialog
        open={showWorkExperienceDialog}
        onClose={() => setShowWorkExperienceDialog(false)}
        onSave={saveWorkExperience}
      />

      {/* Education Dialog */}
      <EducationDialog
        open={showEducationDialog}
        onClose={() => setShowEducationDialog(false)}
        onSave={saveEducation}
      />
    </Box>
  );
};

// Work Experience Dialog Component
const WorkExperienceDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (experience: Omit<WorkExperience, 'id'>) => void;
}> = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState<Omit<WorkExperience, 'id'>>({
    clientName: '',
    website: '',
    fromDate: '',
    toDate: '',
    isPresent: false,
    jobTitle: '',
    jobDescription: '',
    managerName: '',
    managerEmail: '',
    managerPhone: '',
    managerLinkedIn: '',
  });

  const handleSave = () => {
    onSave(formData);
    setFormData({
      clientName: '',
      website: '',
      fromDate: '',
      toDate: '',
      isPresent: false,
      jobTitle: '',
      jobDescription: '',
      managerName: '',
      managerEmail: '',
      managerPhone: '',
      managerLinkedIn: '',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Work Experience</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client Name *"
              value={formData.clientName}
              onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Website *"
              value={formData.website}
              onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Date *"
              placeholder="MM/DD/YYYY"
              value={formData.fromDate}
              onChange={(e) => setFormData(prev => ({ ...prev, fromDate: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="To Date"
              placeholder="MM/DD/YYYY or Present"
              value={formData.toDate}
              onChange={(e) => setFormData(prev => ({ ...prev, toDate: e.target.value }))}
              disabled={formData.isPresent}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Title *"
              value={formData.jobTitle}
              onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Job Description *"
              multiline
              rows={4}
              value={formData.jobDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, jobDescription: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manager Name"
              value={formData.managerName}
              onChange={(e) => setFormData(prev => ({ ...prev, managerName: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manager Email"
              value={formData.managerEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, managerEmail: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manager Phone"
              value={formData.managerPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, managerPhone: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manager LinkedIn"
              value={formData.managerLinkedIn}
              onChange={(e) => setFormData(prev => ({ ...prev, managerLinkedIn: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Experience
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Education Dialog Component
const EducationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (education: Omit<Education, 'id'>) => void;
}> = ({ open, onClose, onSave }) => {
  const educationLevels = [
    'PhD',
    'Masters',
    'Bachelors',
    'Diploma',
    '+12 (Intermediate)',
    'High School'
  ];
  
  const [formData, setFormData] = useState<Omit<Education, 'id'>>({
    institutionName: '',
    level: '',
    fieldOfStudy: '',
    fromMonthYear: '',
    toMonthYear: '',
    gpaScore: '',
  });

  const handleSave = () => {
    onSave(formData);
    setFormData({
      institutionName: '',
      level: '',
      fieldOfStudy: '',
      fromMonthYear: '',
      toMonthYear: '',
      gpaScore: '',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Education</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name of Institution *"
              value={formData.institutionName}
              onChange={(e) => setFormData(prev => ({ ...prev, institutionName: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Level of Education *</InputLabel>
              <Select
                value={formData.level}
                onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
                label="Level of Education *"
              >
                {educationLevels.map((level: string) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Field of Study *"
              value={formData.fieldOfStudy}
              onChange={(e) => setFormData(prev => ({ ...prev, fieldOfStudy: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Month Year *"
              placeholder="MM/YYYY"
              value={formData.fromMonthYear}
              onChange={(e) => setFormData(prev => ({ ...prev, fromMonthYear: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="To Month Year *"
              placeholder="MM/YYYY"
              value={formData.toMonthYear}
              onChange={(e) => setFormData(prev => ({ ...prev, toMonthYear: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="GPA/Score"
              value={formData.gpaScore}
              onChange={(e) => setFormData(prev => ({ ...prev, gpaScore: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Education
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default USATechieSignup;




